import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { 
  Settings, 
  Database, 
  Bell,
  Globe,
  DollarSign,
  Printer,
  Save,
  RotateCcw
} from 'lucide-react'

export function AdminSettings() {
  const [settings, setSettings] = useState({
    restaurant_name: 'Mi Restaurante',
    currency: 'USD',
    tax_rate: '10.00',
    service_charge: '5.00',
    receipt_header: '¡Gracias por visitarnos!',
    receipt_footer: '¡Esperamos verte pronto!',
    notification_email: 'admin@restaurant.com',
    backup_frequency: 'daily',
    theme: 'light',
    language: 'es'
  })

  const handleSave = () => {
    // TODO: Implement settings save
    console.log('Saving settings:', settings)
    alert('¡Configuración guardada correctamente!')
  }

  const handleReset = () => {
    setSettings({
      restaurant_name: 'Mi Restaurante',
      currency: 'USD',
      tax_rate: '10.00',
      service_charge: '5.00',
      receipt_header: '¡Gracias por visitarnos!',
      receipt_footer: '¡Esperamos verte pronto!',
      notification_email: 'admin@restaurant.com',
      backup_frequency: 'daily',
      theme: 'light',
      language: 'es'
    })
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Configuración del sistema</h2>
          <p className="text-muted-foreground">
            Configura los ajustes del sistema POS de tu restaurante
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Restablecer
          </Button>
          <Button onClick={handleSave}>
            <Save className="w-4 h-4 mr-2" />
            Guardar cambios
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Restaurant Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Información del restaurante
            </CardTitle>
            <CardDescription>
              Datos básicos de tu restaurante
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Nombre del restaurante</label>
              <Input
                value={settings.restaurant_name}
                onChange={(e) => setSettings({...settings, restaurant_name: e.target.value})}
                placeholder="Ingresa el nombre del restaurante"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Idioma</label>
              <select
                className="w-full p-2 border border-input rounded-md bg-background"
                value={settings.language}
                onChange={(e) => setSettings({...settings, language: e.target.value})}
              >
                <option value="en">Inglés</option>
                <option value="es">Español</option>
                <option value="fr">Francés</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Financial Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Configuración financiera
            </CardTitle>
            <CardDescription>
              Moneda, impuestos y cargos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Moneda</label>
              <select
                className="w-full p-2 border border-input rounded-md bg-background"
                value={settings.currency}
                onChange={(e) => setSettings({...settings, currency: e.target.value})}
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Tasa de impuesto (%)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings.tax_rate}
                  onChange={(e) => setSettings({...settings, tax_rate: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Cargo por servicio (%)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings.service_charge}
                  onChange={(e) => setSettings({...settings, service_charge: e.target.value})}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Receipt Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Configuración de recibos
            </CardTitle>
            <CardDescription>
              Personaliza la apariencia y mensajes del recibo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Encabezado del recibo</label>
              <Input
                value={settings.receipt_header}
                onChange={(e) => setSettings({...settings, receipt_header: e.target.value})}
                placeholder="Mensaje del encabezado"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Pie del recibo</label>
              <Input
                value={settings.receipt_footer}
                onChange={(e) => setSettings({...settings, receipt_footer: e.target.value})}
                placeholder="Mensaje del pie"
              />
            </div>
          </CardContent>
        </Card>

        {/* System Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuración del sistema
            </CardTitle>
            <CardDescription>
              Comportamiento y preferencias del sistema
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Tema</label>
              <select
                className="w-full p-2 border border-input rounded-md bg-background"
                value={settings.theme}
                onChange={(e) => setSettings({...settings, theme: e.target.value})}
              >
                <option value="light">Claro</option>
                <option value="dark">Oscuro</option>
                <option value="system">Sistema</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Frecuencia de respaldo</label>
              <select
                className="w-full p-2 border border-input rounded-md bg-background"
                value={settings.backup_frequency}
                onChange={(e) => setSettings({...settings, backup_frequency: e.target.value})}
              >
                <option value="hourly">Cada hora</option>
                <option value="daily">Diario</option>
                <option value="weekly">Semanal</option>
                <option value="manual">Solo manual</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notificaciones
            </CardTitle>
            <CardDescription>
              Configura alertas y notificaciones
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Correo de notificaciones</label>
              <Input
                type="email"
                value={settings.notification_email}
                onChange={(e) => setSettings({...settings, notification_email: e.target.value})}
                placeholder="admin@restaurant.com"
              />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">Alertas de stock bajo</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">Reportes diarios</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">Actualizaciones del sistema</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">Notificaciones de error</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Estado del sistema
          </CardTitle>
          <CardDescription>
            Salud e información actual del sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <Badge variant="outline" className="w-full">
                Base de datos
              </Badge>
              <p className="text-sm text-green-600 mt-1">Conectada</p>
            </div>
            <div className="text-center">
              <Badge variant="outline" className="w-full">
                Servidor API
              </Badge>
              <p className="text-sm text-green-600 mt-1">En línea</p>
            </div>
            <div className="text-center">
              <Badge variant="outline" className="w-full">
                Estado del respaldo
              </Badge>
              <p className="text-sm text-green-600 mt-1">Actualizado</p>
            </div>
            <div className="text-center">
              <Badge variant="outline" className="w-full">
                Versión
              </Badge>
              <p className="text-sm text-muted-foreground mt-1">v1.0.0</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
